import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Camera, Check, X, Link2, ExternalLink, Share2 } from "lucide-react";

const ROLES = ["Batter", "Bowler", "All-rounder", "Wicket-keeper"];
const HANDS = ["Right-hand", "Left-hand"];

export default function Profile() {
  const { user } = useAuth();
  const isCoach = user?.role === "coach";
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [links, setLinks] = useState([]);
  const [saving, setSaving] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    const { data } = await api.get("/profile/me"); setProfile(data.profile || {});
    if (!isCoach) { const d = await api.get("/dashboard"); setStats(d.data); }
    const l = await api.get("/coach-links"); setLinks(l.data);
  };
  useEffect(() => { load(); }, []);

  const set = (patch) => setProfile((p) => ({ ...p, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const { role_type, batting_hand, bowling_type, bio, photo_url } = profile;
      await api.put("/profile", isCoach ? { bio } : { role_type, batting_hand, bowling_type, bio, photo_url });
      toast.success("Profile saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "photo");
    try { const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } }); set({ photo_url: `${backend}${data.url}` }); toast.success("Photo uploaded — remember to save"); }
    catch { toast.error("Upload failed"); }
  };

  const actLink = async (id, action) => { await api.put(`/coach-links/${id}`, { action }); load(); toast.success(`Link ${action}d`); };

  const shareCard = async () => {
    const url = `${window.location.origin}/p/${user.username}`;
    const doShare = async () => {
      const shareData = { title: `${user.name} · CrickTrack`, text: "Check out my cricket player card on CrickTrack", url };
      if (navigator.share) {
        try { await navigator.share(shareData); } catch {}
      } else {
        try { await navigator.clipboard.writeText(url); toast.success("Card link copied — send it to your coach"); }
        catch { window.prompt("Copy your card link:", url); }
      }
    };
    if (profile.privacy?.is_public) { doShare(); return; }
    if (window.confirm("Your player card is private. Make it public so your coach can view it?")) {
      const nextPrivacy = { is_public: true, public_fields: profile.privacy?.public_fields || { batting: true, bowling: true, bio: true } };
      await api.put("/profile", { privacy: nextPrivacy });
      setProfile((p) => ({ ...p, privacy: nextPrivacy }));
      toast.success("Your card is now public");
      doShare();
    }
  };

  if (!profile) return <div className="max-w-2xl mx-auto space-y-3">{[1, 2].map((i) => <div key={i} className="skeleton h-32" />)}</div>;
  const strength = profile.profile_strength || 15;

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="profile-page">
      <div className="ct-card p-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-20 h-20 rounded-2xl bg-ink overflow-hidden flex items-center justify-center text-white font-head font-black text-3xl">
              {profile.photo_url ? <img src={profile.photo_url} alt="Profile" className="w-full h-full object-cover" data-testid="profile-photo" /> : user?.name?.[0]}
            </div>
            <label className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-brand text-white flex items-center justify-center cursor-pointer" data-testid="upload-photo-btn">
              <Camera size={14} /><input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
            </label>
          </div>
          <div className="flex-1">
            <h1 className="font-head font-bold text-2xl">{user?.name}</h1>
            <p className="text-slate-500 text-sm">{user?.email} · @{user?.username}</p>
          </div>
        </div>
        {!isCoach && (
          <div className="mt-5">
            <div className="flex justify-between text-xs mb-1"><span className="font-bold uppercase tracking-widest text-slate-500">Profile strength</span><span className="stat-num text-brand">{strength}%</span></div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-brand" style={{ width: `${strength}%` }} /></div>
          </div>
        )}
      </div>

      {!isCoach && stats && (
        <div className="grid grid-cols-3 gap-3" data-testid="headline-stats">
          <Head l="Runs" v={stats.batting.runs} /><Head l="Wickets" v={stats.bowling.wickets} /><Head l="Avg" v={stats.batting.average ?? "-"} />
        </div>
      )}

      <div className="ct-card p-6 space-y-4">
        <h2 className="font-head font-bold text-xl">Edit profile</h2>
        {!isCoach && (
          <>
            <div><Label className="mb-2 block text-xs">Role</Label><div className="flex flex-wrap gap-2">{ROLES.map((r) => <Pill key={r} active={profile.role_type === r} onClick={() => set({ role_type: r })}>{r}</Pill>)}</div></div>
            <div><Label className="mb-2 block text-xs">Batting hand</Label><div className="flex gap-2">{HANDS.map((r) => <Pill key={r} active={profile.batting_hand === r} onClick={() => set({ batting_hand: r })}>{r}</Pill>)}</div></div>
            <div><Label className="text-xs">Bowling type</Label><Input value={profile.bowling_type || ""} onChange={(e) => set({ bowling_type: e.target.value })} className="mt-1" data-testid="edit-bowling-type" /></div>
          </>
        )}
        <div><Label className="text-xs">Bio</Label><textarea className="w-full rounded-lg border border-slate-200 p-2 text-sm mt-1" rows={3} value={profile.bio || ""} onChange={(e) => set({ bio: e.target.value })} data-testid="edit-bio" /></div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-dark" data-testid="save-profile-btn">{saving ? "Saving…" : "Save"}</Button>
          {!isCoach && <a href={`/p/${user?.username}`} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="view-public-btn"><ExternalLink size={14} /> Public page</Button></a>}
          {!isCoach && <Button variant="outline" onClick={shareCard} data-testid="share-card-btn" className="border-brand text-brand hover:bg-brand hover:text-white"><Share2 size={14} /> Share with coach</Button>}
        </div>
      </div>

      {/* coach links */}
      <div className="ct-card p-6" data-testid="coach-links">
        <div className="flex items-center gap-2 mb-3"><Link2 size={16} className="text-brand" /><h2 className="font-head font-bold text-xl">{isCoach ? "Player Links" : "My Coaches"}</h2></div>
        {links.length === 0 ? <p className="text-slate-500 text-sm">No links yet.</p> : (
          <div className="space-y-2">
            {links.map((l) => {
              const other = isCoach ? l.player : l.coach;
              const canRespond = l.status === "pending" && l.initiator !== user.role;
              return (
                <div key={l.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3" data-testid={`link-${l.id}`}>
                  <div><span className="font-semibold text-sm">{other?.name}</span> <span className={`ml-2 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${l.status === "active" ? "bg-green-100 text-green-700" : l.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-500"}`}>{l.status}</span></div>
                  <div className="flex gap-1">
                    {canRespond && <><button onClick={() => actLink(l.id, "approve")} className="p-1.5 bg-green-100 text-green-700 rounded" data-testid={`approve-${l.id}`}><Check size={14} /></button><button onClick={() => actLink(l.id, "reject")} className="p-1.5 bg-red-100 text-brand rounded" data-testid={`reject-${l.id}`}><X size={14} /></button></>}
                    {l.status === "active" && <button onClick={() => actLink(l.id, "revoke")} className="text-xs text-brand font-semibold" data-testid={`revoke-${l.id}`}>Revoke</button>}
                    {l.status === "pending" && !canRespond && <span className="text-xs text-slate-400">Awaiting response</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Pill({ active, onClick, children }) {
  return <button type="button" onClick={onClick} className={`px-4 py-2 rounded-full text-sm font-semibold border-2 ${active ? "border-brand bg-brand text-white" : "border-slate-200 bg-white hover:border-slate-300"}`}>{children}</button>;
}
function Head({ l, v }) { return <div className="ct-card p-4 text-center"><div className="stat-num text-3xl tabular">{v}</div><div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{l}</div></div>; }
